// Core types for the Bureaucromancy rules engine.
// Everything here is plain, JSON-serialisable data. See docs/07-architecture.md §5.

// ─── Primitives ──────────────────────────────────────────────────────────────

export const COLORS = ['red', 'yellow', 'blue', 'green'] as const;
export type Color = (typeof COLORS)[number];

export const SCHOOLS = ['conjuration', 'transmutation', 'evocation', 'divination'] as const;
export type School = (typeof SCHOOLS)[number];

export const SCHOOL_COLOR: Record<School, Color> = {
  conjuration: 'red',
  transmutation: 'yellow',
  evocation: 'blue',
  divination: 'green',
};

export const LOCATION_IDS = [
  'conjuration',
  'transmutation',
  'evocation',
  'archive',
  'divination',
  'enchantment',
  'procuration',
  'mana-conduit',
  'arcanium',
  'planning-office',
  'grand-auditor',
  'precedence',
  // P-18: Annex rooms. Closed at setup; a Resolution opens each into one of the Annex bays.
  'exchange-bureau',
  'lost-property',
  'shredding-room',
  'stamp-pound',
  'complaints-desk',
  'mailroom',
] as const;
export type LocationId = (typeof LOCATION_IDS)[number];

/** The Annex rooms (P-18): locations that exist only while open in an Annex bay. */
export const ANNEX_IDS = ['exchange-bureau', 'lost-property', 'shredding-room', 'stamp-pound', 'complaints-desk', 'mailroom'] as const satisfies readonly LocationId[];
export type AnnexId = (typeof ANNEX_IDS)[number];

export type Permits = Record<Color, number>;
export type PermitBag = Partial<Record<Color, number>>;

export type SeatId = number;
export type CardId = string;
export type DefId = string;

// ─── Effect DSL (content-authored behaviour) ─────────────────────────────────

export type Amount = number | 'archive' | 'planning';

/** Operations interpreted by the engine's stack machine. */
export type Op =
  | { op: 'gain'; red?: number; yellow?: number; blue?: number; green?: number; mana?: number; vp?: number }
  | { op: 'choosePermits'; count: number; colors?: Color[] | 'locationGives' }
  | { op: 'draw'; n: Amount }
  | { op: 'discardActions'; count: number }
  | { op: 'playActions'; max: Amount }
  /** `noRefresh`: a purchase away from the Arcanium (Night Market) offers no refresh. */
  | { op: 'buySpell'; discount?: number; noRefresh?: boolean }
  | { op: 'refreshArcanium' }
  | { op: 'claimProblem' }
  | { op: 'block' }
  | { op: 'extraPlacement' }
  | { op: 'gainAssistant' }
  /** P-11: take the Stamp of Precedence — you go first from the next round on. */
  | { op: 'takeStamp' }
  | { op: 'resolveOwnDepartment' }
  | { op: 'copyOtherDepartment' }
  | { op: 'exchangePermits' }
  | { op: 'liquidate'; max: number; manaEach: number }
  | { op: 'swapSpell' }
  | { op: 'modifier'; kind: TempModifierKind; amount: number }
  // ── interaction (content v0.4) ──
  /** Put a Decree into force (the Gazette holds `decreeSlots`; the oldest is superseded). */
  | { op: 'enact'; decree: DefId }
  /** Strike 1 Decree in force (the player chooses). */
  | { op: 'repeal' }
  /** Put a rival's spell Under Review: its abilities stop until the end of the next round. */
  | { op: 'suspend' }
  /** Return a rival's cast spell to their hand; they lose its printed VP. */
  | { op: 'revoke' }
  /** Each rival (or each rival with more VP than you) pays you up to `mana`. */
  | { op: 'levy'; mana: number; from: 'all' | 'leaders' }
  /** Every player (or every other player) gains the listed resources. */
  | { op: 'allGain'; red?: number; yellow?: number; blue?: number; green?: number; mana?: number; others?: boolean }
  /** Take 1 random Action card from a rival's hand. */
  | { op: 'stealAction' }
  // ── Annex rooms (P-18) ──
  /** Exchange Bureau: return up to `max` of your permits, then gain that many of your choice. */
  | { op: 'returnPermits'; max: number }
  /** Lost Property: take 1 spell from the spell discard into your hand. */
  | { op: 'recoverSpell' }
  /** Shredding Room: discard up to `max` cards from your hands, then gain 1 permit of your choice for each. */
  | { op: 'shred'; max: number }
  /** Mailroom: give 1 Action card from your hand to a rival of your choice. */
  | { op: 'giveAction' }
  // system ops (engine internal; not authored in content)
  /** Resolve a location's effect. `noFlip`: the extra resolution won on a coin flip (it does not flip again). */
  | { op: 'location'; location: LocationId; noFlip?: boolean }
  /** Magical Weather (P-17): flip the Ministry coin; heads resolves the location's effect once more. */
  | { op: 'coinFlip'; location: LocationId }
  | { op: 'lose'; mana: number }
  | { op: 'announce' }
  | { op: 'toDiscard'; card: CardId }
  | { op: 'handLimit' }
  | { op: 'startTurn'; seat: SeatId }
  | { op: 'finishTurn' }
  | { op: 'endRound' }
  | { op: 'endGame' };

export type OpName = Op['op'];

export type TriggerSpec =
  | { event: 'roundStart' }
  | { event: 'permitsGained'; color: Color }
  | { event: 'visit'; location?: LocationId; keyword?: LocationKeyword }
  | { event: 'spellAcquired' }
  | { event: 'problemClaimed'; by: 'self' | 'other' }
  /** After you cast a spell (optionally of one school). A spell never triggers on its own casting. */
  | { event: 'spellCast'; school?: School }
  /** After you play an Action card. */
  | { event: 'actionPlayed' }
  /** A rival places an Assistant (`sharing`: in a location where you already have one). */
  | { event: 'rivalVisit'; sharing?: boolean; location?: LocationId; keyword?: LocationKeyword }
  /** After a rival casts a spell (optionally of one school). */
  | { event: 'rivalCast'; school?: School };

export type Modifier =
  | { type: 'spellCost'; school: School; color: Color; delta: number; min: number }
  | { type: 'archiveDraw'; delta: number }
  | { type: 'handLimit'; value: number }
  | { type: 'arcaniumPrice'; delta: number }
  | { type: 'ignoreCapacity' } // once per round
  | { type: 'countsAsAllSchools' }
  | { type: 'problemVp'; delta: number }
  | { type: 'substitute' } // once per round: pay 1 Yellow as any colour
  | { type: 'manaForPermit' } // once per round: pay 1 mana in place of 1 permit
  | { type: 'firstSpellEachRoundVp'; delta: number }
  | { type: 'freeCast' } // once per game: cast ignoring permit cost
  | { type: 'appealImmune' } // rivals cannot Suspend/Revoke your spells or Repeal your Decrees
  | { type: 'decreeVp'; delta: number }; // your Decrees in force score extra VP at game end

/**
 * Global rules changes carried by Decrees. They apply to every player while the Decree is in force
 * (tolls are paid by everyone except the Decree's author, to the author).
 */
export type GlobalMod =
  | { type: 'locationBonus'; location: LocationId; red?: number; yellow?: number; blue?: number; green?: number; mana?: number }
  | { type: 'toll'; location: LocationId; mana: number }
  | { type: 'archiveDraw'; delta: number }
  | { type: 'planningPlays'; delta: number }
  | { type: 'capacity'; delta: number }
  | { type: 'problemVp'; delta: number }
  | { type: 'upkeep'; mana: number }
  | { type: 'handLimit'; delta: number }
  | { type: 'arcaniumPrice'; delta: number }
  /** Every spell's cost in its own school's colour changes by `delta` (not below `min`). */
  | { type: 'schoolCost'; delta: number; min: number };

export type EndGameMetric =
  | 'evocationSpells'
  | 'conjurationSpells'
  | 'transmutationSpells'
  | 'divinationSpells'
  | 'spellsCast'
  | 'distinctSchools'
  | 'actionCardsPlayed'
  | 'permitsLeft'
  | 'manaLeft'
  | 'problemsClaimed';

export type Limit = 'round' | 'game';

export type Ability =
  | { kind: 'trigger'; on: TriggerSpec; limit?: Limit; effect: Op[] }
  | { kind: 'activated'; label: string; cost?: { mana?: number; discardAction?: number }; limit?: Limit; effect: Op[] }
  | { kind: 'modifier'; mod: Modifier }
  | { kind: 'endGame'; per: EndGameMetric; every?: number; vp: number }
  | { kind: 'onCast'; effect: Op[] };

// ─── Content definitions ─────────────────────────────────────────────────────

export type LocationKeyword = 'department' | 'utility' | 'market' | 'records' | 'objective' | 'annex';

export interface LocationDef {
  id: LocationId;
  kind: 'location';
  name: string;
  keyword: LocationKeyword;
  text: string;
  /** Permit colours this location gives (for "a colour that Department gives"). */
  gives?: Color[] | 'any';
  /** A fixed capacity for this location, whatever the player count or Decrees (P-11: 1). */
  capacity?: number;
  /** P-18: an Annex room — closed until a Resolution opens it into an Annex bay. */
  annex?: boolean;
  effect: Op[];
}

export interface SpellValues {
  price: number;
  cost: PermitBag;
  vp: number;
}

export interface SpellDef extends SpellValues {
  id: DefId;
  kind: 'spell';
  name: string;
  school: School;
  formNo: string;
  text: string;
  flavor: string;
  abilities: Ability[];
  /** v1 values for A/B comparison (rules option `cardValues: 'v1'`). */
  v1?: Partial<SpellValues>;
}

export interface ActionDef {
  id: DefId;
  kind: 'action';
  name: string;
  copies: number;
  text: string;
  flavor: string;
  effect: Op[];
}

export type StatName =
  | 'sameDepartmentRounds'
  | 'departmentsVisited'
  | 'maxSchoolCount'
  | 'distinctSchools'
  | 'problemsClaimed'
  | 'spellsCastByRound3'
  | 'manaLeft'
  | 'permitsLeft'
  | 'actionCardsPlayed'
  | 'spellsBought'
  | 'maxSpellPriceCast'
  | 'conduitRounds'
  | 'auditorVisits'
  | 'reached20BeforeRound7'
  | 'choicePermitsGained'
  | 'spellsCast'
  | 'totalPrintedVp'
  | 'appealsFiled'
  | 'decreesEnacted'
  | 'decreesInForce'
  | 'manaCollected'
  | 'sharedPlacements';

export type Requirement =
  | { castSchool: { school: School; atLeast: number } }
  | { distinctSchools: number }
  | { spellsCast: number }
  | { totalPrintedVp: number }
  | { stat: { name: StatName; atLeast: number } }
  | { permitsEach: number };

export type Condition =
  | { stat: StatName; atLeast?: number; atMost?: number }
  | { all: Condition[] };

export interface ProblemDef {
  id: DefId;
  kind: 'problem';
  name: string;
  description: string;
  text: string;
  requirement?: Requirement;
  fee?: { permits?: PermitBag; mana?: number };
  tiers: [number, number];
  /** P-17: the Resolution enacted for everyone after this Problem's Tier I is claimed. */
  resolution?: DefId;
}

// ─── Resolutions (P-17) ──────────────────────────────────────────────────────

export type ResolutionSpice = 'mild' | 'bold' | 'wild';

/** Rules options a Resolution can depend on (by their proposal codes). */
export type RuleCode = 'P-11' | 'P-12' | 'P-16' | 'P-18';

/** One-time effects when a Resolution is enacted. */
export type EnactOp =
  /** Open an Annex room in an empty bay (the oldest open room closes if both are full). */
  | { op: 'openAnnex'; location: LocationId }
  /** Strike every Decree in force that carries a modifier of this type. */
  | { op: 'strikeDecrees'; modifier: GlobalMod['type'] }
  /** The game now ends after this round, if that is earlier than the current last round. */
  | { op: 'setLastRound'; round: number }
  /** The Interns arrive at the start of the next round, if that is earlier than planned. */
  | { op: 'setInternRound'; round: 'next' }
  /** Every hand becomes public for the rest of the game. */
  | { op: 'revealHands' };

/** Continuous rule changes while a Resolution is in force (for every player). */
export type ResolutionMod =
  /** Any number of permits in a permit cost or fee may be paid with `rate` mana each. */
  | { type: 'substitutePayment'; payWith: 'mana'; rate: number }
  /** A location's own effect also gives this (part of its effect: repeats repeat it). */
  | { type: 'locationBonus'; location: LocationId; red?: number; yellow?: number; blue?: number; green?: number; mana?: number }
  /** Each time a location with this keyword resolves, flip a coin; heads resolves it once more. */
  | { type: 'locationEffectRepeat'; keyword: LocationKeyword; chance: 'coinFlip' }
  /** The first placement of each round resolves its location's effect `times` more. */
  | { type: 'firstPlacementRepeat'; times: number }
  /** Play this many Action cards per round as a free action on your turn. */
  | { type: 'freeActionCard'; perRound: number }
  /** The Arcanium's refresh (P-16) costs this much mana. */
  | { type: 'arcaniumRefreshCost'; value: number }
  /** Decrees carrying this modifier can't be enacted or issued. */
  | { type: 'banDecrees'; modifier: GlobalMod['type'] }
  /** After a location's own effect, these ops resolve too (part of its effect). */
  | { type: 'locationAfter'; location: LocationId; effect: Op[] };

export interface ResolutionDef {
  id: DefId;
  kind: 'resolution';
  name: string;
  spice: ResolutionSpice;
  /** Rules options that must be on, or the Resolution is removed from its Problem at setup. */
  requires: RuleCode[];
  /** One sentence for the card (≤ 110 characters). */
  text: string;
  /** The precise rule, for the rulebook and the tooltip. */
  rules: string;
  flavor: string;
  onEnact: EnactOp[];
  modifiers: ResolutionMod[];
}

/**
 * A Bureaucromancer's starting package (P-15), received when the draft ends: weaker abilities come
 * with more. `choice` permits are picked in the draft, with the identity.
 */
export interface StartingPackage {
  name: string;
  permits?: PermitBag;
  choice?: number;
  actions?: number;
  mana?: number;
}

export interface IdentityDef {
  id: DefId;
  kind: 'identity';
  name: string;
  faction: 'ministry' | 'unfiled';
  role: string;
  abilityName: string;
  text: string;
  abilities: Ability[];
  package?: StartingPackage;
}

export interface AgendaDef {
  id: DefId;
  kind: 'agenda';
  name: string;
  text: string;
  condition: Condition;
  vp: number;
}

export interface DecreeDef {
  id: DefId;
  kind: 'decree';
  name: string;
  text: string;
  flavor: string;
  effects: GlobalMod[];
}

export interface RankDef {
  title: string;
  minVp: number;
}

export interface RulesOptions {
  rounds: number;
  startingAssistants: number;
  /** Starting mana by seat index (P-06). */
  startingMana: number[];
  /** Location capacity by player count (P-01). Keys are player counts as strings. */
  capacity: Record<string, number>;
  /** Archive draw (P-02). */
  archiveDraw: number;
  /** Planning Office plays (P-02). */
  planningPlays: number;
  actionHandLimit: number;
  arcaniumSize: number;
  upkeepMana: number;
  /** P-05: 'playersPlus1' reveals players+1 problems; 'all' reveals the whole pool. */
  problemsRevealed: 'all' | 'playersPlus1';
  /** P-05: second reward tier for a different player. */
  problemTiers: boolean;
  startingSpells: number;
  startingActions: number;
  noLegalLocationMana: number;
  draft: boolean;
  draftIdentities: number;
  draftAgendas: number;
  /** P-03/P-04: use v2 (default) or v1 printed spell values. */
  cardValues: 'v2' | 'v1';
  /** How many Decrees the Gazette holds at once. */
  decreeSlots: number;
  /**
   * P-11, the Office of Precedence: the first player keeps the Stamp of Precedence (and goes first)
   * until someone takes it at the Office. Off (v1): the Office is closed and the Stamp passes left.
   */
  precedenceOffice: boolean;
  /** P-12, the Afternoon Shift: every player gains a third Assistant, the Intern, at this round. */
  internRound: number | null;
  /**
   * Most Assistants a player can have at once, temporary ones included (the Intern raises it by 1
   * from the Afternoon Shift on). Gains beyond it do nothing. null: no cap (v1).
   */
  assistantCap: number | null;
  /** Rounds at whose start the Ministry issues a Decree from the Decree deck (no author). */
  decreeRounds: number[];
  /**
   * P-16: the Arcanium display is no longer cleared at Upkeep; a visitor may pay this much mana to
   * clear and refill it (once per visit) before buying. null: the v1 rule (cleared every round).
   */
  arcaniumRefreshCost: number | null;
  /** VP each Decree still in force scores for its author at game end. */
  decreeVp: number;
  /** P-17: City Problems carry Resolutions, enacted for everyone after their Tier I is claimed. */
  resolutions: boolean;
  /** Which Resolutions are enabled: a named list in the content pack (null: every Resolution). */
  resolutionSet: string | null;
  /** P-18: the Annex rooms. Off: the Resolutions that open them are removed from their Problems. */
  annexRooms: boolean;
  /** How many Annex rooms can be open at once. */
  annexBays: number;
  /** At most this many Wild Resolutions among the revealed Problems (extras are redrawn at setup). */
  maxWildRevealed: number;
  /** P-19: the holder of the Stamp of Precedence may not place at the Office of Precedence. */
  precedenceHolderBan: boolean;
}

export interface ContentPack {
  version: string;
  hash: string;
  locations: LocationDef[];
  spells: SpellDef[];
  actions: ActionDef[];
  problems: ProblemDef[];
  identities: IdentityDef[];
  agendas: AgendaDef[];
  decrees: DecreeDef[];
  /** P-17: the Resolutions printed on City Problems, and named sets of them (`resolutionSet`). */
  resolutions: ResolutionDef[];
  resolutionSets: Record<string, DefId[]>;
  ranks: RankDef[];
  defaults: RulesOptions;
  /** Named option sets merged over `defaults` (e.g. `v1` for A/B simulation). */
  presets: Record<string, Partial<RulesOptions>>;
}

// ─── Game state ──────────────────────────────────────────────────────────────

export type SourceRef =
  | { kind: 'rules' }
  | { kind: 'location'; id: LocationId }
  | { kind: 'card'; card: CardId; def: DefId }
  | { kind: 'identity'; def: DefId }
  | { kind: 'problem'; def: DefId }
  | { kind: 'agenda'; def: DefId }
  | { kind: 'decree'; def: DefId }
  | { kind: 'resolution'; def: DefId };

export interface Assistant {
  id: string;
  temp: boolean;
  /** The Afternoon Shift's Intern (P-12): a permanent Assistant, drawn smaller. */
  intern?: boolean;
  placed: boolean;
  location: LocationId | null;
}

export interface SeatStats {
  /** Rounds in which each location was visited (placements only). */
  visits: Partial<Record<LocationId, number[]>>;
  actionCardsPlayed: number;
  spellsBought: number;
  choicePermitsGained: number;
  castRounds: number[];
  maxCastPrice: number;
  reached20Round: number | null;
  /** Suspensions and revocations this seat filed. */
  appealsFiled: number;
  decreesEnacted: number;
  /** Mana received from rivals (tolls and levies). */
  manaCollected: number;
  /** Placements into a location a rival already occupied. */
  sharedPlacements: number;
}

export interface SeatState {
  seat: SeatId;
  name: string;
  mana: number;
  vp: number;
  rank: number;
  permits: Permits;
  spellHand: CardId[];
  actionHand: CardId[];
  /** Cast spells, in the order they were cast. */
  tableau: CardId[];
  problems: { problem: DefId; tier: 1 | 2; vp: number }[];
  identity: DefId | null;
  agenda: DefId | null;
  draftOptions: { identities: DefId[]; agendas: DefId[] } | null;
  /** The starting package's choice permits, picked in the draft (hidden until it resolves). */
  packageChoice?: PermitBag | null;
  assistants: Assistant[];
  /** Monotonic counter for assistant ids (`s{seat}a{n}`). */
  assistantSerial: number;
  stats: SeatStats;
}

export interface LocationState {
  occupants: string[]; // assistant ids
  blockedBy: SeatId[];
}

export interface ProblemSlot {
  problem: DefId;
  claims: SeatId[];
  /** P-17: this Problem's Resolution in this game (absent: none, or removed at setup). */
  resolution?: DefId;
}

/** A Resolution waiting for the end of the round (queued on a Tier I claim), or in force. */
export interface ResolutionEntry {
  resolution: DefId;
  problem: DefId;
  /** Who claimed the Problem's Tier I. */
  by: SeatId;
  /** The round it was queued in (for one in force: the round it was enacted after). */
  round: number;
}

export interface Zones {
  spellDeck: CardId[];
  spellDiscard: CardId[];
  arcanium: (CardId | null)[];
  actionDeck: CardId[];
  actionDiscard: CardId[];
  problems: ProblemSlot[];
  /** Decrees not in force, face down (the Ministry issues from here), and those struck or superseded. */
  decreeDeck: DefId[];
  decreeDiscard: DefId[];
}

export interface DecreeInForce {
  decree: DefId;
  /** Its author, or null for a Decree the Ministry issued itself (it scores for nobody). */
  by: SeatId | null;
  /** Round it was enacted (oldest is superseded first). */
  round: number;
}

export interface Frame {
  seat: SeatId;
  source: SourceRef;
  ops: Op[];
  i: number;
  env: Record<string, unknown>;
}

/** `nextSpellPermits` (the Stamp Pound): the next spell costs `amount` fewer permits of your choice. */
export type TempModifierKind = 'nextSpellDiscount' | 'nextPurchaseDiscount' | 'nextSpellPermits';

export interface TempModifier {
  seat: SeatId;
  kind: TempModifierKind;
  amount: number;
  source: SourceRef;
}

export interface TurnState {
  seat: SeatId;
  basePlaced: boolean;
}

export interface GameState {
  v: 1;
  meta: {
    rulesVersion: string;
    contentHash: string;
    options: RulesOptions;
    playerCount: number;
  };
  rng: [number, number, number, number];
  phase: 'draft' | 'action' | 'ended';
  round: number;
  firstSeat: SeatId;
  /** Who holds the Stamp of Precedence: the first player of the next round (P-11). */
  stampHolder: SeatId;
  activeSeat: SeatId;
  turn: TurnState | null;
  seats: SeatState[];
  board: Record<LocationId, LocationState>;
  zones: Zones;
  /** Opaque card instance id → definition id. */
  cards: Record<CardId, DefId>;
  stack: Frame[];
  pending: PendingDecision[];
  modifiers: TempModifier[];
  /** Decrees in force, oldest first. */
  decrees: DecreeInForce[];
  /** Spells Under Review → last round (inclusive) of the review. */
  suspended: Record<CardId, number>;
  /** Once-per-round / once-per-game usage keys. */
  usage: Record<string, number>;
  nextDecisionId: number;
  result: GameResult | null;
  /** The game ends after this round (`rounds`; Early Closing brings it forward). */
  lastRound: number;
  /** The round the Interns arrive (P-12; Labour Accord brings it forward), or null. */
  internRound: number | null;
  /** P-17: Resolutions queued this round (claim order) and those in force (enactment order). */
  resolutions: { queued: ResolutionEntry[]; inForce: ResolutionEntry[] };
  /** P-18: the Annex bays (an open room or null) and the rooms in the order they opened. */
  annex: { bays: (LocationId | null)[]; opened: LocationId[] };
  /** The Fog Lifts: every spell and Action hand is public. */
  openHands: boolean;
  /** Morning Rush: this round's first placement has been made. */
  firstPlacementDone: boolean;
}

// ─── Decisions ───────────────────────────────────────────────────────────────

export type PendingDecision =
  /** `choice[i]`: how many starting-package permits of their choice identities[i] lets them pick. */
  | { id: number; seat: SeatId; kind: 'draft'; identities: DefId[]; agendas: DefId[]; choice?: number[] }
  | { id: number; seat: SeatId; kind: 'choosePermits'; count: number; colors: Color[] }
  | { id: number; seat: SeatId; kind: 'discardActions'; count: number; cards: CardId[]; reason: 'effect' | 'handLimit' | 'cost' }
  /** `at`: the room offering the plays (the Planning Office or the Mailroom). */
  | { id: number; seat: SeatId; kind: 'playAction'; cards: CardId[]; remaining: number; at?: LocationId }
  /** `refresh`: the mana it costs to clear and refill the display first (absent when not allowed).
   *  `remote`: a purchase away from the Arcanium (the Night Market, P-17). */
  | { id: number; seat: SeatId; kind: 'buySpell'; options: { card: CardId; price: number }[]; refresh?: number; remote?: true }
  /** `manaPermits`: the fee's permits paid with mana (Currency Reform), when the permits fall short. */
  | { id: number; seat: SeatId; kind: 'claimProblem'; options: ProblemOption[] }
  | { id: number; seat: SeatId; kind: 'chooseLocation'; purpose: 'audit' | 'overtime' | 'notarized'; locations: LocationId[] }
  | { id: number; seat: SeatId; kind: 'exchange'; give: Color[]; targets: { seat: SeatId; colors: Color[] }[] }
  | { id: number; seat: SeatId; kind: 'liquidate'; max: number; manaEach: number }
  | { id: number; seat: SeatId; kind: 'swapSpell'; hand: CardId[]; arcanium: CardId[] }
  | { id: number; seat: SeatId; kind: 'chooseSpell'; purpose: 'suspend' | 'revoke'; options: { card: CardId; seat: SeatId }[] }
  | { id: number; seat: SeatId; kind: 'chooseDecree'; purpose: 'repeal'; decrees: DefId[] }
  | { id: number; seat: SeatId; kind: 'chooseRival'; purpose: 'steal'; seats: SeatId[] }
  /** Exchange Bureau: return up to `max` of your permits (then pick as many of your choice). */
  | { id: number; seat: SeatId; kind: 'returnPermits'; max: number }
  /** Lost Property: take one of these spells from the discard. */
  | { id: number; seat: SeatId; kind: 'recoverSpell'; cards: CardId[] }
  /** Shredding Room: discard up to `max` of these cards (from either hand). */
  | { id: number; seat: SeatId; kind: 'shred'; max: number; spells: CardId[]; actions: CardId[] }
  /** Mailroom: give one of these Action cards to one of these rivals. */
  | { id: number; seat: SeatId; kind: 'giveAction'; cards: CardId[]; seats: SeatId[] };

export interface ProblemOption {
  problem: DefId;
  tier: 1 | 2;
  vp: number;
  /** Currency Reform: the fee's permits paid with mana instead (the least needed). */
  manaPermits?: PermitBag;
}

export type DecisionKind = PendingDecision['kind'];

export type Choice =
  | { identity: DefId; agenda: DefId; permits?: PermitBag } // draft (`permits`: the package's choice permits)
  | { permits: PermitBag } // choosePermits, liquidate, returnPermits
  | { cards: CardId[] } // discardActions, shred
  | { card: CardId; seat: SeatId } // giveAction
  | { card: CardId } // playAction, buySpell, chooseSpell
  | { decree: DefId } // chooseDecree
  | { seat: SeatId } // chooseRival
  | { problem: DefId; manaPermits?: PermitBag } // claimProblem (`manaPermits`: Currency Reform)
  | { location: LocationId } // chooseLocation
  | { target: SeatId; give: Color; take: Color } // exchange
  | { hand: CardId; arcanium: CardId } // swapSpell
  | { refresh: true } // buySpell: pay to clear and refill the Arcanium display first
  | { skip: true }; // optional decisions (playAction "done", buySpell, claimProblem)

// ─── Commands ────────────────────────────────────────────────────────────────

export interface Payment {
  /** Alchemical Reclassification: pay 1 extra Yellow as this colour. */
  yellowAs?: Color;
  /** Margot: pay 1 mana in place of 1 permit of this colour. */
  manaFor?: Color;
  /** Legal Precedent: remove 1 permit of this colour from the cost. */
  discountOn?: Color;
  /** The Archivox: ignore the permit cost entirely. */
  free?: boolean;
  /** The Stamp Pound: remove these permits from the cost (up to its amount, in colours the cost has). */
  reduceBy?: PermitBag;
  /** Currency Reform: pay these permits with mana instead (3 each). */
  manaPermits?: PermitBag;
}

export interface AbilityRef {
  /** 'identity' or a card instance id in the seat's tableau. */
  source: 'identity' | CardId;
  index: number;
}

export type Command =
  | { t: 'place'; location: LocationId }
  | { t: 'cast'; card: CardId; payment: Payment }
  | { t: 'activate'; ability: AbilityRef }
  /** Memos Fly Free (P-17): play an Action card from your hand as a free action, once per round. */
  | { t: 'playAction'; card: CardId }
  | { t: 'decide'; decision: number; choice: Choice }
  /** `skip`: forfeit this turn's placement (the assistant goes home, no compensation). */
  | { t: 'endTurn'; skip?: boolean };

// ─── Events ──────────────────────────────────────────────────────────────────

export interface Standing {
  seat: SeatId;
  vp: number;
  place: number;
  breakdown: { label: string; vp: number }[];
  agenda: DefId | null;
  agendaMet: boolean;
  tiebreak: number[];
}

export interface GameResult {
  standings: Standing[];
  winners: SeatId[];
}

export type GameEvent =
  | { t: 'MatchStarted'; players: { seat: SeatId; name: string }[]; firstSeat: SeatId; problems: DefId[] }
  | { t: 'DraftDealt'; seat: SeatId; identities: DefId[]; agendas: DefId[] }
  | { t: 'AgendaChosen'; seat: SeatId; agenda: DefId | null }
  /** P-15: a player opens their Bureaucromancer's starting package (the gains follow). */
  | { t: 'StartingPackage'; seat: SeatId; identity: DefId }
  | { t: 'DraftResolved'; identities: (DefId | null)[] }
  | { t: 'RoundStarted'; round: number }
  | { t: 'TurnStarted'; seat: SeatId }
  | { t: 'AssistantPlaced'; seat: SeatId; assistant: string; location: LocationId; slot: number; temp: boolean }
  | { t: 'AssistantSentHome'; seat: SeatId; assistant: string; skipped?: boolean }
  | { t: 'AssistantGained'; seat: SeatId; assistant: string; temp: boolean; intern?: boolean }
  /** The Afternoon Shift starts (P-12): each player's Intern follows as an AssistantGained. */
  | { t: 'InternsHired'; round: number }
  /** P-11: the Stamp of Precedence changes hands (`from` goes first no longer, from next round). */
  | { t: 'StampTaken'; seat: SeatId; from: SeatId }
  | { t: 'AssistantRemoved'; seat: SeatId; assistant: string }
  | { t: 'AssistantsRetrieved' }
  | { t: 'PermitsGained'; seat: SeatId; permits: PermitBag; source: SourceRef }
  | { t: 'PermitsSpent'; seat: SeatId; permits: PermitBag; reason: 'cast' | 'fee' | 'exchange' | 'liquidate' }
  | { t: 'ManaGained'; seat: SeatId; amount: number; source: SourceRef }
  | { t: 'ManaSpent'; seat: SeatId; amount: number; reason: 'purchase' | 'cast' | 'fee' | 'ability' | 'refresh' }
  /** A visitor paid to clear and refill the Arcanium display (P-16); ArcaniumCleared/Refilled follow. */
  | { t: 'ArcaniumRefreshed'; seat: SeatId; mana: number }
  | { t: 'VPGained'; seat: SeatId; amount: number; source: SourceRef }
  | { t: 'RankPromoted'; seat: SeatId; rank: number; title: string }
  /** `open`: drawn while hands are public (The Fog Lifts): everyone sees `def`. */
  | { t: 'CardDrawn'; seat: SeatId; card: CardId; def?: DefId; deck: 'action' | 'spell'; open?: boolean }
  /** `fromHand`: discarded from a hand (hand limit, costs, effects) vs. a played card going to the discard pile. */
  | { t: 'CardDiscarded'; seat: SeatId; card: CardId; def: DefId; deck: 'action' | 'spell'; fromHand: boolean }
  | { t: 'DeckShuffled'; deck: 'action' | 'spell' | 'decree'; size: number }
  | { t: 'SpellPurchased'; seat: SeatId; card: CardId; def: DefId; price: number; slot: number }
  | { t: 'ArcaniumCleared'; cards: { card: CardId; def: DefId }[] }
  | { t: 'ArcaniumRefilled'; slot: number; card: CardId; def: DefId }
  | { t: 'SpellCast'; seat: SeatId; card: CardId; def: DefId; payment: Payment; spent: PermitBag; mana: number }
  | { t: 'AbilityTriggered'; seat: SeatId; source: SourceRef }
  | { t: 'AbilityUsed'; seat: SeatId; source: SourceRef; index: number }
  /** `free`: played as a free action (Memos Fly Free), not at the Planning Office. */
  | { t: 'ActionCardPlayed'; seat: SeatId; card: CardId; def: DefId; free?: boolean }
  | { t: 'LocationBlocked'; seat: SeatId; location: LocationId }
  | { t: 'PermitsExchanged'; seat: SeatId; target: SeatId; gave: Color; took: Color }
  | { t: 'SpellSwapped'; seat: SeatId; handCard: CardId; handDef: DefId; arcCard: CardId; arcDef: DefId; slot: number }
  | { t: 'ProblemClaimed'; seat: SeatId; problem: DefId; tier: 1 | 2; vp: number }
  | { t: 'ModifierAdded'; seat: SeatId; kind: TempModifierKind; amount: number }
  | { t: 'DecreeEnacted'; seat: SeatId; decree: DefId; superseded: DefId | null }
  /** `resolution`: struck by a Resolution (Free Roads); `seat` is then the Resolution's claimer. */
  | { t: 'DecreeRepealed'; seat: SeatId; decree: DefId; author: SeatId | null; resolution?: DefId }
  /** A Decree that a Resolution bans was cast: it does nothing (Free Roads). */
  | { t: 'DecreeFizzled'; seat: SeatId; decree: DefId; resolution: DefId }
  /** The Ministry issues a Decree from the deck at the start of a round (no author). */
  | { t: 'DecreeIssued'; decree: DefId; superseded: DefId | null }
  /** An Assistant gain did nothing: the player already has the most Assistants allowed. */
  | { t: 'AssistantCapped'; seat: SeatId; cap: number }
  | { t: 'SpellSuspended'; seat: SeatId; owner: SeatId; card: CardId; def: DefId; until: number }
  | { t: 'SpellReinstated'; owner: SeatId; card: CardId; def: DefId }
  | { t: 'SpellRevoked'; seat: SeatId; owner: SeatId; card: CardId; def: DefId }
  /** Mana moved from `seat` to `to` (a toll on visiting, or a levy). */
  | { t: 'ManaPaid'; seat: SeatId; to: SeatId; amount: number; reason: 'toll' | 'levy' }
  /** `def` is visible only to the two players involved (to everyone while hands are public: `open`). */
  | { t: 'ActionStolen'; seat: SeatId; from: SeatId; card: CardId; def?: DefId; open?: boolean }
  // ── Resolutions and Annex rooms (P-17, P-18) ──
  /** A Tier I claim queued its Problem's Resolution for the end of the round. */
  | { t: 'ResolutionQueued'; seat: SeatId; problem: DefId; resolution: DefId }
  /** A Resolution comes into force for everyone (`order`: 1 for the first in force this game). */
  | { t: 'ResolutionEnacted'; seat: SeatId; problem: DefId; resolution: DefId; order: number }
  /** Magical Weather: the Ministry coin, flipped for a Department visit. Heads: it resolves again. */
  | { t: 'CoinFlipped'; seat: SeatId; location: LocationId; result: 'heads' | 'tails' }
  /** An Annex room opens in a bay (`replaced`: the room that closed to make way, its card out of the game). */
  | { t: 'AnnexOpened'; location: LocationId; bay: number; replaced: LocationId | null; resolution: DefId }
  /** The Fog Lifts: every hand, face up. */
  | { t: 'HandsRevealed'; hands: { seat: SeatId; spells: { card: CardId; def: DefId }[]; actions: { card: CardId; def: DefId }[] }[] }
  /** Mailroom: an Action card passes to a rival. `def` is visible only to the two players (or to all: `open`). */
  | { t: 'ActionCardGiven'; seat: SeatId; to: SeatId; card: CardId; def?: DefId; open?: boolean }
  /** Lost Property: a spell comes back from the discard into a hand. */
  | { t: 'SpellRecovered'; seat: SeatId; card: CardId; def: DefId }
  | { t: 'DecisionRequested'; seat: SeatId; decision: number; kind: DecisionKind }
  | { t: 'DecisionResolved'; seat: SeatId; decision: number; kind: DecisionKind }
  | { t: 'TurnEnded'; seat: SeatId }
  | { t: 'RoundEnded'; round: number }
  | { t: 'FirstPlayerPassed'; seat: SeatId }
  | { t: 'GameEnded'; result: GameResult; agendas: (DefId | null)[] }
  // Emitted by MatchRunner (not the engine) when a turn is rewound by undo.
  | { t: 'TurnRewound'; seat: SeatId; toSeq: number };

export type GameEventType = GameEvent['t'];

// ─── Errors ──────────────────────────────────────────────────────────────────

export type RuleErrorCode =
  | 'GAME_OVER'
  | 'WRONG_PHASE'
  | 'NOT_YOUR_TURN'
  | 'DECISION_PENDING'
  | 'NO_SUCH_DECISION'
  | 'INVALID_CHOICE'
  | 'UNKNOWN_LOCATION'
  | 'LOCATION_FULL'
  | 'LOCATION_CLOSED'
  | 'LOCATION_BLOCKED'
  | 'NO_ASSISTANT_AVAILABLE'
  | 'ALREADY_PLACED'
  | 'MUST_PLACE'
  | 'NOT_IN_HAND'
  | 'INSUFFICIENT_PERMITS'
  | 'INSUFFICIENT_MANA'
  | 'INVALID_PAYMENT'
  | 'NO_SUCH_ABILITY'
  | 'ABILITY_USED'
  | 'ABILITY_COST'
  | 'NOTHING_TO_UNDO'
  | 'UNKNOWN_COMMAND'
  /** P-19: the Stamp's holder may not place at the Office of Precedence. */
  | 'HOLDS_STAMP'
  /** A free action that only a Resolution in force allows (e.g. Memos Fly Free). */
  | 'NOT_IN_FORCE'
  | 'FREE_ACTION_USED';

export interface RuleError {
  code: RuleErrorCode;
  params?: Record<string, string | number>;
}

export type Result<T> = { ok: true; value: T } | { ok: false; error: RuleError };

export interface Step {
  state: GameState;
  events: GameEvent[];
}

export interface MatchSetup {
  players: { name: string }[];
  options?: Partial<RulesOptions>;
}
